import React from 'react';
import NewProjectWizardCard from './NewProjectWizardCard';
import { draftFromGroup, groupAccessLines } from './accessGroupCatalog';
import { getInitials } from '../../utils/commonUtils';
import { emptyNdaDraft, NDA_MODE, TEAM_SETUP_STEPS } from './ndaDraft';
import styles from './css/index.module.css';

export default function SetupReviewTeam({
    user,
    groups = [],
    extras = {},
    workstreams = [],
    invitesByGroup = {},
    adminEmails = [],
    ndaDraft = emptyNdaDraft(),
    sending = false,
    onFinish,
    onBack,
    onEdit,
    onEditNda,
}) {
    const displayName = user?.full_name || '';
    const email = user?.email || '';
    const initials = getInitials(displayName) || (email ? email.slice(0, 2).toUpperCase() : '');

    return (
        <NewProjectWizardCard
            step={4}
            totalSteps={TEAM_SETUP_STEPS}
            accessWide
            title="Review team & access"
            subtitle="Confirm who will be invited, the access they will inherit, and any NDA they must accept."
            continueLabel="Finish setup & send invitations"
            continueLoading={sending}
            onContinue={onFinish}
            onBack={onBack}
            backLabel="Back"
            backInline
        >
            <p className={styles.agGroupLabel}>Project Administrators</p>
            <div className={styles.agAdminCard}>
                <div className={styles.agAdminTop}>
                    <span className={styles.agAvatar} aria-hidden="true">
                        {initials}
                    </span>
                    <div>
                        <p className={styles.agAdminName}>
                            {displayName || email || 'You'}
                            <span className={styles.wsRecommendedBadge}>You</span>
                        </p>
                        {email ? <p className={styles.agAdminEmail}>{email}</p> : null}
                    </div>
                </div>
                {adminEmails.map(item => (
                    <PendingMember key={item} email={item} />
                ))}
            </div>

            {groups.map(group => {
                const extra = extras[group.id] || draftFromGroup(group, workstreams);
                const lines = groupAccessLines(extra, workstreams);
                const emails = invitesByGroup[group.id] || [];
                return (
                    <div key={group.id} className={styles.tmReviewCard}>
                        <div className={styles.tmReviewHead}>
                            <h3 className={styles.agGroupName}>{group.name}</h3>
                            <button type="button" className={styles.agTextLink} onClick={onEdit}>
                                Edit
                            </button>
                        </div>
                        <div className={styles.tmReviewBody}>
                            <p className={styles.agGroupLabel}>Members</p>
                            {emails.length ? (
                                emails.map(item => <PendingMember key={item} email={item} />)
                            ) : (
                                <p className={styles.agMetaMuted}>No members added yet</p>
                            )}
                            <div className={styles.tmReviewGrid}>
                                <div>
                                    <p className={styles.tmReviewKey}>Workspace access</p>
                                    <p className={styles.tmReviewValue}>{lines.features || '—'}</p>
                                </div>
                                <div>
                                    <p className={styles.tmReviewKey}>Workstreams</p>
                                    <p className={styles.tmReviewValue}>{lines.workstreamsLabel}</p>
                                </div>
                                <div>
                                    <p className={styles.tmReviewKey}>Intelligence Insights</p>
                                    <p className={lines.intelligenceLabel === 'No access' ? styles.agMetaMuted : styles.tmReviewValue}>
                                        {lines.intelligenceLabel}
                                    </p>
                                </div>
                                <div>
                                    <p className={styles.tmReviewKey}>Project Mail</p>
                                    <p className={lines.projectMailLabel === 'No access' ? styles.agMetaMuted : styles.tmReviewValue}>
                                        {lines.projectMailLabel}
                                    </p>
                                </div>
                                <div>
                                    <p className={styles.tmReviewKey}>Project Meetings</p>
                                    <p className={lines.projectMeetingsLabel === 'No access' ? styles.agMetaMuted : styles.tmReviewValue}>
                                        {lines.projectMeetingsLabel}
                                    </p>
                                </div>
                                <div>
                                    <p className={styles.tmReviewKey}>VDR permissions</p>
                                    <p className={styles.tmReviewValue}>{lines.vdrTitle}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}

            <div className={styles.tmReviewCard}>
                <div className={styles.tmReviewHead}>
                    <h3 className={styles.agGroupName}>NDA</h3>
                    <button type="button" className={styles.agTextLink} onClick={onEditNda || onEdit}>
                        Edit
                    </button>
                </div>
                <div className={styles.tmReviewBody}>
                    {ndaDraft.mode === NDA_MODE.NONE || !ndaDraft.name ? (
                        <p className={styles.agMetaMuted}>No NDA required</p>
                    ) : (
                        <>
                            <p className={styles.tmReviewNdaName}>{ndaDraft.name}</p>
                            <p className={styles.tmReviewNdaHint}>Invitees must accept this NDA before accessing the project.</p>
                            {ndaDraft.documentUrl ? (
                                <button
                                    type="button"
                                    className={styles.ndaViewLink}
                                    onClick={() => window.open(ndaDraft.documentUrl, '_blank', 'noopener,noreferrer')}
                                >
                                    View NDA →
                                </button>
                            ) : null}
                        </>
                    )}
                </div>
            </div>
        </NewProjectWizardCard>
    );
}

const PendingMember = ({ email }) => (
    <div className={styles.tmMemberRow}>
        <span className={styles.tmMemberAvatar} aria-hidden="true">
            {(email || 'U').slice(0, 1).toUpperCase()}
        </span>
        <div>
            <p className={styles.tmPendingEmail}>{email}</p>
            <p className={styles.tmPendingHint}>Will be invited</p>
        </div>
    </div>
);

